import type { ApiError } from "@entrilia/shared/api/types";
import type {
  ContactEmailChangeOutcome,
  ContactEmailChangePlan,
  ContactEmailOwnership,
} from "../../../../../api/types/fundStructureTypes";

export type EmailChangePersonAnswer = "same_person" | "someone_new";

export const isValidEmailChangePersonAnswer = (value: string): value is EmailChangePersonAnswer =>
  value === "same_person" || value === "someone_new";

interface DialogState {
  saveEnabled: boolean;
  noticeShown: boolean;
  lookupFailed: boolean;
  payload?: { isSamePerson: boolean; historyTransferConfirmed: boolean };
}

export const getInvitationDisabledReason = (input: {
  hasPortalAccess: boolean;
  isInvitationRelevant: boolean;
}): string | undefined => {
  if (!input.hasPortalAccess) {
    return "Give this contact access to your portal first — an invitation links to a portal the contact cannot open yet.";
  }
  if (!input.isInvitationRelevant) {
    return "The contact keeps the same login, so there is no new address to invite.";
  }

  return undefined;
};

export const resolveDialogState = (input: {
  answer?: EmailChangePersonAnswer;
  ownership?: ContactEmailOwnership;
  historyTransferConfirmed: boolean;
}): DialogState => {
  const { answer, ownership, historyTransferConfirmed } = input;
  const lookupFailed = ownership === "Unknown";
  const noticeShown = ownership === "Taken";

  if (!answer || !ownership || lookupFailed || (noticeShown && !historyTransferConfirmed)) {
    return { saveEnabled: false, noticeShown, lookupFailed };
  }

  return {
    saveEnabled: true,
    noticeShown,
    lookupFailed,
    payload: { isSamePerson: answer === "same_person", historyTransferConfirmed },
  };
};

export const isInvitationRelevant = (input: {
  answer?: EmailChangePersonAnswer;
  ownership?: ContactEmailOwnership;
  plan: ContactEmailChangePlan;
}): boolean => {
  const { answer, ownership, plan } = input;
  if (!answer) {
    return false;
  }
  if (answer === "someone_new") {
    return true;
  }

  return ownership === "Taken" || plan.samePersonOutcomeAtFreeAddress === "Split";
};

export const keepInvitationChecked = (input: {
  checked: boolean;
  answer?: EmailChangePersonAnswer;
  ownership?: ContactEmailOwnership;
  plan: ContactEmailChangePlan;
}): boolean => input.checked && isInvitationRelevant(input);

export const getEmailChangePersonAnswerExplanation = (
  answer: EmailChangePersonAnswer | undefined,
  plan: ContactEmailChangePlan
): string | undefined => {
  if (answer === "someone_new") {
    return "The previous holder will no longer be able to open this contact. The new person will register with this address.";
  }

  if (answer !== "same_person") {
    return undefined;
  }

  return plan.samePersonOutcomeAtFreeAddress === "Split"
    ? "The contact registers again with the new address, and the portal history moves across."
    : "The contact keeps the same password and everything in the portal, and simply signs in with the new address.";
};

const getOutcomeMessage = (outcome: ContactEmailChangeOutcome, contactName: string): string => {
  switch (outcome) {
    case "Rename":
      return `Email changed. ${contactName} keeps the existing login; we've tried to notify both addresses.`;
    case "Split":
      return `Email changed. ${contactName} will need to register with the new address.`;
    case "Attach":
      return "This contact has been transferred. The new holder can sign in with their existing Entrilia account.";
  }
};

export const getEmailChangeSuccessMessage = (
  outcome: ContactEmailChangeOutcome,
  contactName: string,
  invitation: { requested: boolean; sent: boolean }
): string => {
  const outcomeMessage = getOutcomeMessage(outcome, contactName);
  if (!invitation.requested || outcome === "Rename") {
    return outcomeMessage;
  }

  return invitation.sent
    ? `${outcomeMessage} The invitation email was sent.`
    : `${outcomeMessage} The invitation email couldn't be sent.`;
};

export const isAddressNowOwnedError = (error: ApiError): boolean =>
  error.type === "AlreadyExist" && error.message.includes("already belongs to an Entrilia user");

// submissionIsCurrent must be the same answer that decides whether the transfer notice is shown, or the message can point at a confirmation the dialog is not rendering.
export const getEmailChangeErrorMessage = (error: ApiError, submissionIsCurrent: boolean): string => {
  if (isAddressNowOwnedError(error)) {
    return submissionIsCurrent
      ? "That address now belongs to an Entrilia user. Confirm the transfer below if you want to hand this contact over."
      : "That address now belongs to an Entrilia user, so the change was refused.";
  }

  if (error.type === "PreconditionFailed" && error.message.includes("already has a contact in this company")) {
    return "That person already has their own contact here, so this transfer isn't possible.";
  }

  // A second guard reports the same situation from the contact side, and reaches the caller first.
  if (error.type === "AlreadyExist" && error.message.includes("already exists")) {
    return "Another contact in this company already uses that address, so this change isn't possible.";
  }

  return error.message;
};
